/**
 * Compact binary encoding of a list for share links (decision 018):
 *
 *   ratings     varint count, then per rating: string name, 3 bytes RGB
 *   categories  varint count, then per category:
 *                 string name, varint role count, strings, varint kink count, strings
 *   selections  one byte per kink × role (rating index), in category order —
 *               or nothing, when they travel separately (`packSelections`)
 *   comments    varint count, then per comment: varint kink index, string text
 *   title       string
 *
 * Strings are a varint byte length followed by UTF-8. The structure comes
 * first so that, compressed with a dictionary of the default list's
 * structure, an unchanged default list costs only a few bytes.
 */
import type { IdGen } from '../model/ids';
import { normalizeComment, normalizeName, normalizeTitle } from '../model/text';
import type { Category, KinklistDoc, Rating } from '../model/types';
import { LIMITS } from '../model/types';
import { concatBytes, utf8Decode, utf8Encode } from './bytes';
import { ImportError } from './errors';

export const BINARY_LIMITS = { ratings: 64, categories: 500, kinks: 5000 } as const;

class Writer {
  readonly #chunks: Uint8Array[] = [];

  varint(value: number): this {
    const bytes: number[] = [];
    let n = value;
    do {
      const low = n & 0x7f;
      n = Math.floor(n / 128);
      bytes.push(n > 0 ? low | 0x80 : low);
    } while (n > 0);
    this.#chunks.push(Uint8Array.from(bytes));
    return this;
  }

  bytes(values: ArrayLike<number>): this {
    this.#chunks.push(Uint8Array.from(values));
    return this;
  }

  string(value: string): this {
    const encoded = utf8Encode(value);
    return this.varint(encoded.length).bytes(encoded);
  }

  done(): Uint8Array<ArrayBuffer> {
    return concatBytes(this.#chunks);
  }
}

class Reader {
  #at = 0;
  readonly #data: Uint8Array;

  constructor(data: Uint8Array) {
    this.#data = data;
  }

  get done(): boolean {
    return this.#at === this.#data.length;
  }

  byte(): number {
    const value = this.#data[this.#at];
    if (value === undefined) throw new ImportError('corrupt', 'Unexpected end of data');
    this.#at++;
    return value;
  }

  varint(max: number): number {
    let value = 0;
    let scale = 1;
    for (let i = 0; i < 5; i++) {
      const byte = this.byte();
      value += (byte & 0x7f) * scale;
      if ((byte & 0x80) === 0) {
        if (value > max)
          throw new ImportError('invalid', `Count ${String(value)} exceeds ${String(max)}`);
        return value;
      }
      scale *= 128;
    }
    throw new ImportError('corrupt', 'Varint too long');
  }

  bytes(length: number): Uint8Array {
    if (this.#at + length > this.#data.length)
      throw new ImportError('corrupt', 'Unexpected end of data');
    const slice = this.#data.subarray(this.#at, this.#at + length);
    this.#at += length;
    return slice;
  }

  string(maxBytes: number): string {
    return utf8Decode(this.bytes(this.varint(maxBytes)));
  }
}

const rgb = (color: string) => [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
const hex = (bytes: Uint8Array) =>
  `#${Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** Ratings + categories only: what the compression dictionary is built from. */
export function encodeStructure(
  ratings: readonly Pick<Rating, 'name' | 'color'>[],
  categories: readonly { name: string; roles: readonly string[]; kinks: readonly string[] }[],
): Uint8Array<ArrayBuffer> {
  const w = new Writer().varint(ratings.length);
  for (const rating of ratings) w.string(rating.name).bytes(rgb(rating.color));
  w.varint(categories.length);
  for (const category of categories) {
    w.string(category.name).varint(category.roles.length);
    for (const role of category.roles) w.string(role);
    w.varint(category.kinks.length);
    for (const kink of category.kinks) w.string(kink);
  }
  return w.done();
}

/** Rating index of every kink × role, in category order. */
export function selectionDigits(doc: KinklistDoc): number[] {
  const ratingIndex = new Map(doc.ratings.map((rating, i) => [rating.id, i]));
  return doc.categories.flatMap((category) =>
    category.kinks.flatMap((kink) =>
      category.roles.map((role) => ratingIndex.get(kink.ratings[role.id] ?? '') ?? 0),
    ),
  );
}

/** Byte length of `slots` base-`base` digits packed into one integer. */
export function packedLength(base: number, slots: number): number {
  return slots === 0 ? 0 : Math.ceil((BigInt(base) ** BigInt(slots) - 1n).toString(2).length / 8);
}

/** Packs base-`base` digits into the fewest bytes (little-endian integer). */
export function packSelections(digits: readonly number[], base: number): Uint8Array<ArrayBuffer> {
  let value = 0n;
  const b = BigInt(base);
  for (let i = digits.length - 1; i >= 0; i--) value = value * b + BigInt(digits[i] ?? 0);
  const out = new Uint8Array(packedLength(base, digits.length));
  for (let i = 0; i < out.length; i++) {
    out[i] = Number(value & 0xffn);
    value >>= 8n;
  }
  return out;
}

export function unpackSelections(bytes: Uint8Array, base: number, slots: number): number[] {
  if (bytes.length !== packedLength(base, slots))
    throw new ImportError('corrupt', 'Bad selection data');
  let value = 0n;
  for (let i = bytes.length - 1; i >= 0; i--) value = (value << 8n) | BigInt(bytes[i] ?? 0);
  if (slots > 0 && value >= BigInt(base) ** BigInt(slots))
    throw new ImportError('corrupt', 'Bad selection data');
  const b = BigInt(base);
  return Array.from({ length: slots }, () => {
    const digit = Number(value % b);
    value /= b;
    return digit;
  });
}

export interface BinaryOptions {
  /** Include comments (default true). */
  comments?: boolean;
  /** Write selections inline (default true); off when they're packed separately. */
  selections?: boolean;
}

export function encodeBinary(
  doc: KinklistDoc,
  options: BinaryOptions = {},
): Uint8Array<ArrayBuffer> {
  const structure = encodeStructure(
    doc.ratings,
    doc.categories.map((c) => ({
      name: c.name,
      roles: c.roles.map((r) => r.name),
      kinks: c.kinks.map((k) => k.name),
    })),
  );
  const comments: [number, string][] = [];
  doc.categories
    .flatMap((category) => category.kinks)
    .forEach((kink, i) => {
      if (kink.comment !== undefined && options.comments !== false)
        comments.push([i, kink.comment]);
    });
  const selections = options.selections === false ? [] : selectionDigits(doc);
  const w = new Writer().bytes(structure).bytes(selections).varint(comments.length);
  for (const [index, text] of comments) w.varint(index).string(text);
  return w.string(doc.title).done();
}

function requireName(value: string): string {
  const name = normalizeName(value);
  if (name === null) throw new ImportError('invalid', 'Blank name');
  return name;
}

/**
 * Decodes `encodeBinary` output. `packed` supplies selections that travel
 * separately (see `packSelections`) instead of inline.
 */
export function decodeBinary(
  data: Uint8Array,
  ctx: { newId: IdGen; now: () => Date },
  packed?: Uint8Array,
): KinklistDoc {
  const r = new Reader(data);
  const nameBytes = LIMITS.name * 4;
  const ratingCount = r.varint(BINARY_LIMITS.ratings);
  if (ratingCount < LIMITS.minRatings) throw new ImportError('invalid', 'Too few ratings');
  const ratings: Rating[] = Array.from({ length: ratingCount }, () => ({
    id: ctx.newId(),
    name: requireName(r.string(nameBytes)),
    color: hex(r.bytes(3)),
  }));

  const categoryCount = r.varint(BINARY_LIMITS.categories);
  let totalKinks = 0;
  const categories: Category[] = [];
  for (let c = 0; c < categoryCount; c++) {
    const name = requireName(r.string(nameBytes));
    const roleCount = r.varint(LIMITS.maxRoles);
    if (roleCount < LIMITS.minRoles) throw new ImportError('invalid', 'Category without roles');
    const roles = Array.from({ length: roleCount }, () => ({
      id: ctx.newId(),
      name: requireName(r.string(nameBytes)),
    }));
    const kinkCount = r.varint(BINARY_LIMITS.kinks - totalKinks);
    totalKinks += kinkCount;
    const kinks = Array.from({ length: kinkCount }, () => ({
      id: ctx.newId(),
      name: requireName(r.string(nameBytes)),
      ratings: {},
    }));
    categories.push({ id: ctx.newId(), name, roles, kinks });
  }

  const flat = categories.flatMap((category) => category.kinks.map((kink) => ({ category, kink })));
  const slots = flat.reduce((n, { category }) => n + category.roles.length, 0);
  const digits = packed ? unpackSelections(packed, ratingCount, slots) : null;
  let slot = 0;
  for (const { category, kink } of flat) {
    for (const role of category.roles) {
      const index = digits ? (digits[slot] ?? 0) : r.byte();
      slot++;
      const rating = ratings[index];
      if (!rating) throw new ImportError('invalid', 'Rating index out of range');
      kink.ratings[role.id] = rating.id;
    }
  }

  const commentCount = r.varint(flat.length);
  for (let i = 0; i < commentCount; i++) {
    const target = flat[r.varint(flat.length - 1)];
    const text = normalizeComment(r.string(LIMITS.comment * 4));
    if (target && text !== undefined) target.kink.comment = text;
  }
  const title = normalizeTitle(r.string(LIMITS.title * 4));
  if (!r.done) throw new ImportError('corrupt', 'Trailing data');
  return { v: 3, title, ratings, categories, updatedAt: ctx.now().toISOString() };
}
